import type {
	DesignPreset,
	DesignPresetProperty,
	DesignPresetPropertyValue,
	DesignPresetValue,
} from 'common/design-tokens';
import type { StoryDefaultPlatform } from 'types/story';

export const PRESET_PROPERTY_GROUPS = [
	{ id: 'border', label: 'Border', properties: ['borderStyle', 'borderWidth', 'borderColor'] },
	{
		id: 'radius',
		label: 'Radius',
		properties: [
			'borderRadius',
			'borderTopLeftRadius',
			'borderTopRightRadius',
			'borderBottomRightRadius',
			'borderBottomLeftRadius',
		],
	},
	{ id: 'outerSpace', label: 'Outer space', properties: ['marginTop', 'marginRight', 'marginBottom', 'marginLeft'] },
	{
		id: 'innerSpace',
		label: 'Inner space',
		properties: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'],
	},
] as const satisfies readonly { id: string; label: string; properties: readonly DesignPresetProperty[] }[];
export type PresetPropertyGroup = {
	id: (typeof PRESET_PROPERTY_GROUPS)[number]['id'];
	label: string;
	properties: readonly DesignPresetProperty[];
};
export type PresetAssignmentChange = { property: DesignPresetProperty; value: DesignPresetValue | undefined };

/** Preserve authored row order and keep newly added empty groups at their insertion point. */
export function getPresetPropertyRowKeys(preset: DesignPreset, addedRows: readonly string[]): string[] {
	const existing = Object.keys(preset.properties).map(
		property =>
			PRESET_PROPERTY_GROUPS.find(group => group.properties.some(item => item === property))?.id ?? property
	);
	return [...new Set([...existing.filter(key => !addedRows.includes(key)), ...addedRows])];
}

/** Applies one user action atomically, without materializing other properties/platforms. */
export function updatePresetAssignments(
	preset: DesignPreset,
	platform: 'base' | StoryDefaultPlatform,
	changes: readonly PresetAssignmentChange[]
): DesignPreset {
	const properties = { ...preset.properties };
	for (const change of changes) {
		const assignment: DesignPresetPropertyValue = {
			...properties[change.property],
			platforms: { ...properties[change.property]?.platforms },
		};
		if (platform === 'base') {
			if (change.value === undefined) delete assignment.base;
			else assignment.base = change.value;
		} else if (change.value === undefined) delete assignment.platforms![platform];
		else assignment.platforms![platform] = change.value;
		if (!Object.keys(assignment.platforms ?? {}).length) delete assignment.platforms;
		if (assignment.base === undefined && !assignment.platforms) delete properties[change.property];
		else properties[change.property] = assignment;
	}
	return { ...preset, properties };
}
